import { LoadingIndicator } from "@components/LoadingIndicator";
import { Modal } from "@components/Modal";
import { css } from "@emotion/react";
import { useStatsigClient } from "@statsig/react-bindings";
import { UUID } from "io-ts-types/UUID";
import dynamic from "next/dynamic";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";

import { DONATION_THANK_YOU_MESSAGE_MAX_LENGTH } from "@every.org/common/src/codecs/nonprofit";
import { SafeInt } from "@every.org/common/src/codecs/number";
import { Currency, FundraiserType } from "@every.org/common/src/entity/types";
import {
  getRoutePath,
  ClientRouteName,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import {
  notUndefined,
  removeUndefinedValues,
} from "@every.org/common/src/helpers/objectUtilities";
import {
  createYouTubeEmbedUrl,
  extractYouTubeVideoId,
} from "@every.org/common/src/helpers/socialNetworks";
import {
  postDonationMatchCampaignRouteSpec,
  updateDonationMatchCampaignRouteSpec,
} from "@every.org/common/src/routes/campaign";

import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "src/components/Button";
import { Card } from "src/components/Card";
import { CharacterCounter } from "src/components/CharacterCounter";
import { CoverPhotoOrVideoPicker } from "src/components/CoverPhotoOrVideoPicker";
import type { CoverPhotoOrVideoPickerValidationErrors } from "src/components/CoverPhotoOrVideoPicker/types";
import { Icon, IconSize, IconDisplay } from "src/components/Icon";
import { TextInputLabelText } from "src/components/InputContainer";
import { InputWithSideDescriptionContainer } from "src/components/Nonprofit/EditForm/shared";
import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import { UsersContext } from "src/context/UsersContext";
import { getUser, userOrUndefined } from "src/context/UsersContext/selectors";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { CancelConfirmationModal } from "src/pages/Fundraiser/EditForm/CancelConfirmationModal";
import { DeleteConfirmationModal } from "src/pages/Fundraiser/EditForm/DeleteConfirmationModal";
import { EndConfirmationModal } from "src/pages/Fundraiser/EditForm/EndConfirmationModal";
import { FundraiserOwnerPicker } from "src/pages/Fundraiser/EditForm/FundraiserOwnerPicker";
import FundraiserValidationError from "src/pages/Fundraiser/EditForm/FundraiserValidationError";
import { GoalInput } from "src/pages/Fundraiser/EditForm/GoalInput";
import {
  FundraiserMatchCampaignEdit,
  MatchCampaignCard,
  matchCampaignInfoCheck,
} from "src/pages/Fundraiser/EditForm/MatchCampaignCard";
import { MultiNonprofitLaunchModal } from "src/pages/Fundraiser/EditForm/MultiNonprofitLaunchModal";
import { ProTipBadge } from "src/pages/Fundraiser/EditForm/ProTipBadge";
import { RaisedOfflineAmountInput } from "src/pages/Fundraiser/EditForm/RaisedOfflineInput";
import { SubmitErrorMessage } from "src/pages/Fundraiser/EditForm/SubmitErrorMessage";
import {
  FUNDRAISER_TITLE_MAX_LENGTH,
  createValidation,
  postFundraiser,
  updateFundraiser,
} from "src/pages/Fundraiser/EditForm/actions";
import {
  StyledTextInput,
  StyledTextArea,
  StyledInputDescription,
  InputWithLabelContainer,
  ActionButtonsContainer,
  TopActionsContainer,
  rotateCss,
} from "src/pages/Fundraiser/EditForm/styles";
import {
  FundraiserEditFormKind,
  FundraiserEditFormProps,
  FundraiserValidationErrors,
} from "src/pages/Fundraiser/EditForm/types";
import {
  canEditOrEndFundraiser,
  getNonprofitIdsWhereUserIsAdmin,
  isFundraiserAdmin,
} from "src/pages/Fundraiser/fundraiserPermissions";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { TextSize, textSizeCss } from "src/theme/text";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";

const RichTextEditor = dynamic(() => import("@components/RichTextEditor"), {
  loading: () => <LoadingIndicator />,
  ssr: false,
});

export function FundraiserEditForm({
  nonprofit,
  fundraiser,
  forNonprofits,
  kind,
  onUnsavedChangesChange,
}: FundraiserEditFormProps) {
  const { push: routerPush } = useEdoRouter();
  const { getDynamicConfig } = useStatsigClient();

  const loggedInUser = useLoggedInUserOrUndefined();
  const usersState = useContext(UsersContext);
  const fundraiserCreator =
    fundraiser?.creatorUserId &&
    userOrUndefined(getUser(usersState, { id: fundraiser?.creatorUserId }));
  const [active, setActive] = useState(fundraiser?.active || false);
  const [title, setTitle] = useState(fundraiser?.title || "");
  const [description, setDescription] = useState(fundraiser?.description || "");
  const [coverImageCloudinaryId, setCoverImageCloudinaryId] = useState(
    fundraiser?.coverImageCloudinaryId || nonprofit.coverImageCloudinaryId
  );
  const [coverYoutubeVideoId, setCoverYoutubeVideoId] = useState<
    string | null | undefined
  >(
    extractYouTubeVideoId(
      fundraiser?.metadata?.coverYoutubeVideoUrl || undefined
    )
  );

  const [creatorNonprofitId, setCreatorNonprofitId] = useState<UUID | null>(
    fundraiser?.creatorNonprofitId || null
  );

  const [goal, setGoal] = useState<SafeInt | undefined | null>(
    fundraiser?.goalAmount
  );

  const [donationThankYouMessage, setDonationThankYouMessage] = useState(
    fundraiser?.metadata?.donationThankYouMessage
  );
  const [coverImageAltText, setCoverImageAltText] = useState(
    fundraiser?.metadata?.coverImageAltText
  );

  const [raisedOffline, setRaisedOffline] = useState<
    SafeInt | undefined | null
  >(fundraiser?.raisedData?.raisedOffline);
  const [matchCampaignEdit, setMatchCampaignEdit] =
    useState<FundraiserMatchCampaignEdit>({
      id: undefined,
      maxTotalMatchAmount: 0 as SafeInt,
      currentTotalMatchAmount: undefined,
      maxMatchPerDonationOneTimeAmount: undefined,
      dateRange: [null, null],
      active: false,
      deleted: false,
    });

  const [submitting, setSubmiting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMultiNonprofitLaunchModal, setMultiNonprofitLaunchModal] =
    useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(
    kind === FundraiserEditFormKind.EDIT ? true : false
  );

  const [submitError, setSubmitError] = useState<string>();
  const [validationErrors, setValidationErrors] =
    useState<FundraiserValidationErrors>();

  const setCoverPhotoOrVideoPickerValidationErrors = useCallback(
    (
      value: React.SetStateAction<
        CoverPhotoOrVideoPickerValidationErrors | undefined
      >
    ) => {
      setValidationErrors((prev) => {
        const currentValidationErrors = {
          coverYoutubeVideoUrl: prev?.coverYoutubeVideoUrl,
          hasCoverOption: prev?.hasCoverOption,
        };
        const nextValidationErrors =
          typeof value === "function" ? value(currentValidationErrors) : value;

        return {
          ...prev,
          coverYoutubeVideoUrl: nextValidationErrors?.coverYoutubeVideoUrl,
          hasCoverOption: nextValidationErrors?.hasCoverOption,
        };
      });
    },
    []
  );

  const [saved, setSaved] = useState(false);
  const [isMatchingCampaignModalOpen, setIsMatchingCampaignModalOpen] =
    useState(false);
  // Whether or not this fundraiser has an associated matching campaign
  const [hasMatchingCampaign, setHasMatchingCampaign] = useState(
    kind === FundraiserEditFormKind.EDIT &&
      (fundraiser?.matchCampaigns?.length ?? 0) > 0
  );

  const isMultiNonprofitFundraiser = useMemo(() => {
    return (
      fundraiser?.type === FundraiserType.MULTIPLE_NONPROFITS ||
      (!!forNonprofits && forNonprofits.length > 1)
    );
  }, [forNonprofits, fundraiser]);

  const adminForIds = useMemo(
    () =>
      getNonprofitIdsWhereUserIsAdmin(
        isMultiNonprofitFundraiser ? forNonprofits ?? [] : [nonprofit.id],
        loggedInUser
      ),
    [forNonprofits, isMultiNonprofitFundraiser, nonprofit.id, loggedInUser]
  );

  const isNonprofitAdmin = adminForIds.length > 0;

  const canEdit =
    fundraiser &&
    canEditOrEndFundraiser(fundraiser, loggedInUser) &&
    kind === FundraiserEditFormKind.EDIT;

  const isAdmin = !!fundraiser && isFundraiserAdmin(fundraiser, loggedInUser);

  const canPickOwner =
    (kind === FundraiserEditFormKind.CREATE && isNonprofitAdmin) ||
    (isAdmin && !!canEdit);

  useEffect(() => {
    setCreatorNonprofitId((prev) =>
      fundraiser ? prev : isNonprofitAdmin ? adminForIds[0] : null
    );
  }, [fundraiser, isNonprofitAdmin, adminForIds]);

  const paymentInfoCheck = useCallback(
    (text?: string) => {
      if (text && text !== "" && !isNonprofitAdmin) {
        let isInvalid = false;
        try {
          const fundraiserBlockRegex = new RegExp(
            getDynamicConfig("fundraiser_block_regex").get(
              "regex",
              "(([0-9]){11,}|paypal.me|SWIFT)"
            )
          );
          isInvalid = fundraiserBlockRegex.test(text);
        } catch {
          logger.fatal({
            message:
              "Failed to create RegExp in paymentInfoCheck, skipped testing if the fundraiser contains payment",
            data: { fundraiserSlug: fundraiser?.slug },
          });
        }

        if (isInvalid) {
          throw new FundraiserValidationError({
            message:
              "Sorry, Every.org cannot raise money for individual people - only for nonprofit organizations. Please contact us if this is incorrect.",
            validation: createValidation({ title, description }),
          });
        }
      }
    },
    [getDynamicConfig, description, title, fundraiser?.slug, isNonprofitAdmin]
  );

  const updateBody = useMemo(() => {
    if (fundraiser) {
      const body = removeUndefinedValues({
        active,
        title,
        description,
        coverImageCloudinaryId,
        creatorNonprofitId,
        goal,
        metadata: {
          donationThankYouMessage,
          coverImageAltText,
          coverYoutubeVideoUrl: createYouTubeEmbedUrl(coverYoutubeVideoId),
        },
        raisedOffline,
      });
      if (Object.keys(body).length === 0) {
        return undefined;
      }
      return body;
    }
    return undefined;
  }, [
    coverImageCloudinaryId,
    creatorNonprofitId,
    description,
    fundraiser,
    goal,
    title,
    active,
    donationThankYouMessage,
    coverImageAltText,
    raisedOffline,
    coverYoutubeVideoId,
  ]);

  const hasUnsavedChanges = useMemo(() => {
    if (!fundraiser) {
      return false;
    }
    return (
      active !== (fundraiser.active || false) ||
      title !== (fundraiser.title || "") ||
      description !== (fundraiser.description || "") ||
      coverImageCloudinaryId !==
        (fundraiser.coverImageCloudinaryId ||
          nonprofit.coverImageCloudinaryId) ||
      creatorNonprofitId !== (fundraiser.creatorNonprofitId || null) ||
      goal !== fundraiser.goalAmount ||
      donationThankYouMessage !==
        fundraiser.metadata?.donationThankYouMessage ||
      coverImageAltText !== fundraiser.metadata?.coverImageAltText ||
      coverYoutubeVideoId !==
        extractYouTubeVideoId(
          fundraiser.metadata?.coverYoutubeVideoUrl || undefined
        ) ||
      raisedOffline !== fundraiser.raisedData?.raisedOffline
    );
  }, [
    active,
    title,
    description,
    coverImageCloudinaryId,
    creatorNonprofitId,
    goal,
    donationThankYouMessage,
    coverImageAltText,
    coverYoutubeVideoId,
    raisedOffline,
    fundraiser,
    nonprofit.coverImageCloudinaryId,
  ]);

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  const onSubmit = useCallback(async () => {
    setSubmiting(true);
    setValidationErrors(undefined);
    setSubmitError(undefined);

    const hasCoverOption = isMultiNonprofitFundraiser
      ? !!(
          coverYoutubeVideoId ||
          coverImageCloudinaryId ||
          nonprofit.coverImageCloudinaryId
        )
      : true; // Validate only for multi-nonprofit fundraisers

    // Validate form inputs before proceeding
    const validation = createValidation({
      title,
      description,
      coverYoutubeVideoUrl: createYouTubeEmbedUrl(coverYoutubeVideoId),
      matchCampaign: matchCampaignEdit,
      maxMatchPerDonationOneTimeAmount:
        matchCampaignEdit?.maxMatchPerDonationOneTimeAmount,
      hasCoverOption: hasCoverOption,
    });

    const hasValidationErrors = Object.values(validation).some(notUndefined);

    if (hasValidationErrors) {
      setValidationErrors(validation);
      setMultiNonprofitLaunchModal(false);
      setSubmitError("Please fix the errors in the form and resubmit");
      setSubmiting(false);
      return;
    }

    try {
      if (fundraiser && updateBody) {
        paymentInfoCheck(updateBody.description);
        const updateMatchCampaignBody = removeUndefinedValues({
          maxTotalMatchAmount: matchCampaignEdit?.maxTotalMatchAmount,
          maxMatchPerDonationOneTimeAmount:
            matchCampaignEdit?.maxMatchPerDonationOneTimeAmount,
          startDate: matchCampaignEdit?.dateRange[0]
            ? matchCampaignEdit.dateRange[0]
            : undefined,
          endDate: matchCampaignEdit?.dateRange[1]
            ? matchCampaignEdit.dateRange[1]
            : undefined,
          active: matchCampaignEdit?.active,
        });
        if (matchCampaignEdit.id) {
          if (matchCampaignEdit.active) {
            matchCampaignEdit?.id &&
              matchCampaignInfoCheck({
                fundraiser,
                hasMatchingCampaign,
                matchCampaign: matchCampaignEdit,
              });
            await queryApi(updateDonationMatchCampaignRouteSpec, {
              routeTokens: {
                identifier: matchCampaignEdit.id,
              },
              body: updateMatchCampaignBody,
              queryParams: {},
            });
          }
        } else if (hasMatchingCampaign) {
          matchCampaignInfoCheck({
            fundraiser,
            hasMatchingCampaign,
            matchCampaign: matchCampaignEdit,
          });
          if (matchCampaignEdit.maxTotalMatchAmount) {
            await queryApi(postDonationMatchCampaignRouteSpec, {
              routeTokens: {},
              body: {
                maxTotalMatchAmount: matchCampaignEdit?.maxTotalMatchAmount,
                maxMatchPerDonationOneTimeAmount:
                  matchCampaignEdit?.maxMatchPerDonationOneTimeAmount ?? null,
                startDate: matchCampaignEdit?.dateRange[0],
                endDate: matchCampaignEdit?.dateRange[1],
                fundraiserId: fundraiser.id,
              },
              queryParams: {},
            });
          }
        }
        const response = await updateFundraiser(
          fundraiser.id,
          fundraiser.nonprofitId,
          updateBody,
          nonprofit,
          loggedInUser?.id
        );
        setSaved(true);
        routerPush(
          getRoutePath({
            name: ClientRouteName.FUNDRAISER,
            format: URLFormat.RELATIVE,
            tokens: {
              nonprofitSlug: nonprofit.primarySlug,
              fundraiserSlug: response.slug,
            },
            query: {
              postEdit: "true",
            },
          })
        );
      } else {
        paymentInfoCheck(description);
        matchCampaignInfoCheck({
          fundraiser,
          hasMatchingCampaign: hasMatchingCampaign,
          matchCampaign: matchCampaignEdit,
        });
        const response = await postFundraiser(
          nonprofit.id,
          {
            title,
            description,
            creatorNonprofitId,
            coverImageCloudinaryId,
            goal: goal ?? null,
            metadata: {
              donationThankYouMessage,
              coverYoutubeVideoUrl: createYouTubeEmbedUrl(coverYoutubeVideoId),
            },
            raisedOffline: raisedOffline ?? null,
            matchCampaign:
              hasMatchingCampaign && matchCampaignEdit.maxTotalMatchAmount
                ? {
                    maxTotalMatchAmount: matchCampaignEdit.maxTotalMatchAmount,
                    maxMatchPerDonationOneTimeAmount:
                      matchCampaignEdit.maxMatchPerDonationOneTimeAmount ??
                      null,
                    startDate:
                      matchCampaignEdit.dateRange[0] === null
                        ? new Date()
                        : matchCampaignEdit.dateRange[0],
                    endDate: matchCampaignEdit.dateRange[1],
                  }
                : undefined,
          },
          nonprofit,
          isMultiNonprofitFundraiser ? forNonprofits : undefined,
          loggedInUser?.id
        );
        setSaved(true);
        routerPush(
          getRoutePath({
            name: ClientRouteName.FUNDRAISER,
            format: URLFormat.RELATIVE,
            tokens: {
              nonprofitSlug: nonprofit.primarySlug,
              fundraiserSlug: response.slug,
            },
            query: {
              share: "true",
            },
          })
        );
      }
    } catch (e) {
      setMultiNonprofitLaunchModal(false);
      if (e instanceof FundraiserValidationError) {
        setValidationErrors(e.validation);
      }
      if (e instanceof Error) {
        setSubmitError(e.message);
      }
    }
    setSubmiting(false);
  }, [
    fundraiser,
    updateBody,
    nonprofit,
    loggedInUser?.id,
    routerPush,
    title,
    description,
    creatorNonprofitId,
    coverImageCloudinaryId,
    goal,
    donationThankYouMessage,
    raisedOffline,
    coverYoutubeVideoId,
    paymentInfoCheck,
    matchCampaignEdit,
    hasMatchingCampaign,
    isMultiNonprofitFundraiser,
    forNonprofits,
  ]);

  const actionButtons = useMemo(
    () => (
      <ActionButtonsContainer>
        <Button
          data-tname="FundraiseEdit_Cancel"
          disabled={submitting}
          role={ButtonRole.TEXT_ONLY}
          size={ButtonSize.SMALL}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: () => {
              setShowCancelModal(true);
            },
          }}
        >
          Cancel
        </Button>
        <Button
          data-tname="FundraiseEdit_Launch"
          data-bloom-block={
            isMultiNonprofitFundraiser && kind === FundraiserEditFormKind.CREATE
              ? undefined
              : true
          }
          disabled={
            !nonprofit.donationsEnabled ||
            submitting ||
            (kind === FundraiserEditFormKind.EDIT && !updateBody)
          }
          submitting={submitting}
          role={ButtonRole.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={
            isMultiNonprofitFundraiser && kind === FundraiserEditFormKind.CREATE
              ? {
                  kind: ButtonTargetKind.FUNCTION,
                  action: () => {
                    setMultiNonprofitLaunchModal(true);
                  },
                }
              : { kind: ButtonTargetKind.SUBMIT }
          }
        >
          <div css={horizontalStackCss.xs}>
            {saved && (
              <Icon
                iconImport={() =>
                  import("src/components/Icon/icons/CheckMarkIcon")
                }
                size={IconSize.SMALL}
                display={IconDisplay.CURRENT_COLOR}
              />
            )}
            <span>
              {kind === FundraiserEditFormKind.CREATE
                ? saved
                  ? "Launched"
                  : "Launch"
                : saved
                ? "Saved"
                : "Save"}
            </span>
          </div>
        </Button>
      </ActionButtonsContainer>
    ),
    [
      submitting,
      kind,
      saved,
      updateBody,
      nonprofit.donationsEnabled,
      isMultiNonprofitFundraiser,
    ]
  );

  const endFundraiser = async () => {
    setSubmiting(true);
    setValidationErrors(undefined);
    setSubmitError(undefined);
    setActive(!active);

    try {
      if (fundraiser && updateBody) {
        updateBody.active = !active;
        const response = await updateFundraiser(
          fundraiser.id,
          fundraiser.nonprofitId,
          updateBody
        );
        setSaved(true);
        routerPush(
          getRoutePath({
            name: ClientRouteName.FUNDRAISER,
            format: URLFormat.RELATIVE,
            tokens: {
              nonprofitSlug: nonprofit.primarySlug,
              fundraiserSlug: response.slug,
            },
          })
        );
      }
    } catch (e) {
      if (e instanceof FundraiserValidationError) {
        setValidationErrors(e.validation);
      }
      if (e instanceof Error) {
        setSubmitError(e.message);
      }
    }
    setSubmiting(false);
  };

  const advancedSection = (
    <div css={verticalStackCss.xs}>
      <TextInputLabelText htmlFor="thankYouMessage">
        Thank you message
      </TextInputLabelText>
      <InputWithSideDescriptionContainer>
        <StyledTextArea
          name="thankYouMessage"
          data-tname="thankYouMessage"
          id="thankYouMessage"
          validationStatus={validationErrors?.donationThankYouMessage}
          value={donationThankYouMessage || undefined}
          onChange={(e) => {
            setValidationErrors((prev) => ({
              ...prev,
              donationThankYouMessage: undefined,
            }));
            setDonationThankYouMessage(e.currentTarget.value);
          }}
          description={
            <CharacterCounter
              length={donationThankYouMessage?.length || 0}
              maxLength={DONATION_THANK_YOU_MESSAGE_MAX_LENGTH}
              hideCount
            />
          }
          rows={4}
        />
        <span>
          Write a custom message that will be included in every receipt so
          supporters of this fundraiser know how much{" "}
          {isMultiNonprofitFundraiser
            ? "you appreciate them supporting these organizations"
            : isNonprofitAdmin
            ? "they mean to your organization"
            : "you appreciate them supporting this organization"}
          .
        </span>
      </InputWithSideDescriptionContainer>
      <TextInputLabelText htmlFor="raisedOfflineAmountInput">
        Amount raised offline
      </TextInputLabelText>
      <InputWithSideDescriptionContainer>
        <RaisedOfflineAmountInput
          raisedOffline={raisedOffline}
          setRaisedOffline={setRaisedOffline}
          currency={fundraiser?.raisedData?.currency || Currency.USD}
        />
        <span>
          If you are fundraising off of Every.org, for example by accepting cash
          donations, you can input the total amount raised offline to include it
          in your fundraiser&apos;s progress bar.
        </span>
      </InputWithSideDescriptionContainer>
      {(canEdit || canPickOwner) && (
        <FundraiserOwnerPicker
          loggedInUser={loggedInUser}
          fundraiserCreator={fundraiserCreator ? fundraiserCreator : undefined}
          canPickOwner={canPickOwner}
          adminForIds={adminForIds}
          isActive={fundraiser?.active}
          creatorNonprofitId={creatorNonprofitId}
          onChange={(id) => setCreatorNonprofitId(id)}
          setShowDeleteModal={setShowDeleteModal}
          setShowEndModal={setShowEndModal}
          kind={kind}
          canEdit={canEdit}
        />
      )}
    </div>
  );

  const matchingCampaignModal = (
    <Modal
      isOpen={isMatchingCampaignModalOpen}
      onRequestClose={() => setIsMatchingCampaignModalOpen(false)}
    >
      <div css={[verticalStackCss.s, { maxWidth: "400px" }]}>
        <h2
          css={css`
            ${textSizeCss[TextSize.m]}
          `}
        >
          {hasMatchingCampaign
            ? "Delete matching campaign?"
            : "Add a donation match"}
        </h2>
        {hasMatchingCampaign ? (
          <p>
            This cannot be undone, but you can add a new matching campaign
            later.
          </p>
        ) : (
          <p>
            If you have matching funds for donations we can display each donor’s
            matched amount when they make their donation. Every.org does not
            provide the matching funds.
          </p>
        )}
        <div
          css={[
            css`
              display: flex;
              flex-direction: column-reverse;
              gap: ${spacing.l};
            `,
            cssForMediaSize({
              min: MediaSize.MEDIUM_SMALL,
              css: css`
                flex-direction: row;
                justify-content: right;
              `,
            }),
          ]}
        >
          <Button
            data-tname="MatchingCampaignModal_cancel"
            role={ButtonRole.TEXT_ONLY}
            disabled={submitting}
            onClick={{
              kind: ButtonTargetKind.FUNCTION,
              action: () => setIsMatchingCampaignModalOpen(false),
            }}
          >
            Cancel
          </Button>
          <Button
            data-tname="MatchingCampaignModal_submit"
            data-bloom-block
            role={ButtonRole.PRIMARY}
            submitting={submitting}
            disabled={submitting}
            onClick={{
              kind: ButtonTargetKind.FUNCTION,
              action: async () => {
                setIsMatchingCampaignModalOpen(false);
                if (hasMatchingCampaign && matchCampaignEdit.id) {
                  await queryApi(updateDonationMatchCampaignRouteSpec, {
                    routeTokens: {
                      identifier: matchCampaignEdit.id,
                    },
                    body: {
                      deleted: !matchCampaignEdit.deleted,
                    },
                    queryParams: {},
                  });
                  setMatchCampaignEdit((matchCampaign) => {
                    return {
                      ...matchCampaign,
                      deleted: !matchCampaignEdit.deleted,
                    };
                  });
                } else {
                  setHasMatchingCampaign(!hasMatchingCampaign);
                }
              },
            }}
          >
            {hasMatchingCampaign && !matchCampaignEdit.deleted
              ? "Delete match"
              : "We have matching funds"}
          </Button>
        </div>
      </div>
    </Modal>
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      css={verticalStackCss.xl}
    >
      <TopActionsContainer>
        <div>{/* TODO: add folders */}</div>
        {actionButtons}
      </TopActionsContainer>
      <SubmitErrorMessage error={submitError} />
      <Card css={verticalStackCss.l}>
        <CoverPhotoOrVideoPicker
          defaultCoverImageId={nonprofit.coverImageCloudinaryId}
          coverImageAltText={coverImageAltText}
          setCoverImageAltText={setCoverImageAltText}
          validationErrors={validationErrors}
          setValidationErrors={setCoverPhotoOrVideoPickerValidationErrors}
          coverYoutubeVideoId={coverYoutubeVideoId}
          setCoverYoutubeVideoId={setCoverYoutubeVideoId}
          coverImageId={coverImageCloudinaryId}
          setCoverImageId={setCoverImageCloudinaryId}
          allowDefaultCoverImage
        />
        <InputWithLabelContainer>
          <TextInputLabelText htmlFor="title">Title</TextInputLabelText>
          <InputWithSideDescriptionContainer>
            <StyledTextInput
              id="title"
              data-tname="title"
              value={title}
              onChange={(e) => {
                setTitle(e.currentTarget.value);
                setValidationErrors((prev) => ({ ...prev, title: undefined }));
              }}
              description={
                <CharacterCounter
                  length={title?.length || 0}
                  maxLength={FUNDRAISER_TITLE_MAX_LENGTH}
                  hideCount
                />
              }
              validationStatus={validationErrors?.title}
            />
            <StyledInputDescription>
              <ProTipBadge />
              <span>
                Try focusing on the mission of the nonprofit. For example,
                &quot;Help Protect Wildlife in Arizona&quot; or &quot;Every Kid
                Deserves To Read&quot;
              </span>
            </StyledInputDescription>
          </InputWithSideDescriptionContainer>
        </InputWithLabelContainer>
        <InputWithLabelContainer>
          <TextInputLabelText htmlFor="description">
            Description
          </TextInputLabelText>
          <InputWithSideDescriptionContainer>
            <RichTextEditor
              data-tname="description"
              value={description || ""}
              onChange={(value) => {
                setDescription(value);
                setValidationErrors((prev) => ({
                  ...prev,
                  description: undefined,
                }));
              }}
              validationStatus={validationErrors?.description}
            />
            <StyledInputDescription>
              <ProTipBadge />
              <span>
                Share stories about why this is important to you, metrics about
                the impact of donations, or details about who will benefit.
              </span>
            </StyledInputDescription>
          </InputWithSideDescriptionContainer>
        </InputWithLabelContainer>
        <GoalInput
          setGoal={setGoal}
          goal={goal}
          currency={fundraiser?.goalCurrency || Currency.USD}
        />
      </Card>
      <div css={verticalStackCss.s}>
        <Button
          data-tname="showMoreFundraiser-OptionalSettings"
          role={ButtonRole.TEXT_ONLY}
          size={ButtonSize.LARGE}
          css={css`
            align-self: flex-start;
            padding: 0 ${spacing.l};
          `}
          contentCss={horizontalStackCss.xs}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: () => setShowAdvanced(!showAdvanced),
          }}
        >
          {showAdvanced ? (
            <React.Fragment>
              <span>Hide advanced settings</span>
              <Icon
                iconImport={() =>
                  import("@components/Icon/icons/SelectArrowIcon")
                }
                css={[rotateCss]}
                size={IconSize.MEDIUM}
                display={IconDisplay.CURRENT_COLOR}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <span>Advanced settings</span>
              <Icon
                iconImport={() =>
                  import("@components/Icon/icons/SelectArrowIcon")
                }
                size={IconSize.MEDIUM}
                display={IconDisplay.CURRENT_COLOR}
              />
            </React.Fragment>
          )}
        </Button>
        {showAdvanced && (
          <div
            css={css`
              ${verticalStackCss.l}
              padding-top: ${spacing.m};
            `}
          >
            {(!hasMatchingCampaign ||
              (matchCampaignEdit.id && matchCampaignEdit.deleted)) && (
              <Button
                data-tname="addMatch-OptionalSettings"
                role={ButtonRole.SECONDARY}
                size={ButtonSize.LARGE}
                css={css`
                  align-self: flex-start;
                `}
                onClick={{
                  kind: ButtonTargetKind.FUNCTION,
                  action: () => setIsMatchingCampaignModalOpen(true),
                }}
              >
                Add matching campaign
              </Button>
            )}
            {hasMatchingCampaign && !matchCampaignEdit.deleted && (
              <MatchCampaignCard
                fundraiser={fundraiser}
                matchCampaignEdit={matchCampaignEdit}
                setMatchCampaignEdit={setMatchCampaignEdit}
                setIsMatchingCampaignModalOpen={setIsMatchingCampaignModalOpen}
                kind={kind}
                setHasMatchingCampaign={setHasMatchingCampaign}
                setShowAdvanced={setShowAdvanced}
                setSubmitError={setSubmitError}
                setValidationErrors={setValidationErrors}
                validationErrors={validationErrors}
              />
            )}
            <Card>{advancedSection}</Card>
            {matchingCampaignModal}
          </div>
        )}
      </div>
      {kind === FundraiserEditFormKind.CREATE && (
        <div
          css={css`
            text-align: left;
            padding: 0 ${spacing.l};
          `}
        >
          Funds will be sent to {nonprofit.name}, not to you. Your name and
          email will be shared with {nonprofit.name} so they can thank you, but
          no money will be sent to you. Every.org can only fundraise for
          organizations, not individuals.
        </div>
      )}
      <div
        css={[
          horizontalStackCss,
          css`
            align-items: center;
            flex-wrap: wrap;
          `,
        ]}
      >
        <SubmitErrorMessage error={submitError} />
        {actionButtons}
      </div>
      <CancelConfirmationModal
        isOpen={showCancelModal}
        onReject={() => setShowCancelModal(false)}
        editKind={kind}
        onConfirm={() =>
          routerPush(
            fundraiser && kind === FundraiserEditFormKind.EDIT
              ? getRoutePath({
                  name: ClientRouteName.FUNDRAISER,
                  format: URLFormat.RELATIVE,
                  tokens: {
                    nonprofitSlug: nonprofit.primarySlug,
                    fundraiserSlug: fundraiser.slug,
                  },
                })
              : getRoutePath({
                  name: ClientRouteName.NONPROFIT_OR_CAUSE,
                  format: URLFormat.RELATIVE,
                  tokens: {
                    nonprofitSlug: nonprofit.primarySlug,
                  },
                })
          )
        }
      />
      {!!forNonprofits?.length && (
        <MultiNonprofitLaunchModal
          isOpen={showMultiNonprofitLaunchModal}
          onReject={() => setMultiNonprofitLaunchModal(false)}
          onConfirm={onSubmit}
        />
      )}
      {fundraiser && (
        <EndConfirmationModal
          isOpen={showEndModal}
          onReject={() => setShowEndModal(false)}
          nonprofitName={nonprofit.name}
          fundraiserId={fundraiser.id}
          nonprofitId={fundraiser.nonprofitId}
          isActive={active}
          onConfirm={endFundraiser}
        />
      )}
      {fundraiser && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onReject={() => setShowDeleteModal(false)}
          nonprofitName={nonprofit.name}
          fundraiserId={fundraiser.id}
          nonprofitId={fundraiser.nonprofitId}
          onConfirm={() =>
            routerPush(
              getRoutePath({
                name: ClientRouteName.NONPROFIT_OR_CAUSE,
                format: URLFormat.RELATIVE,
                tokens: {
                  nonprofitSlug: nonprofit.primarySlug,
                },
              })
            )
          }
        />
      )}
    </form>
  );
}
